import { Component, OnInit } from '@angular/core';
import { AppointmentDataService } from '../utils/data-service';
import { AssetAPIService } from 'src/app/extensions/services/api/asset-api.service';
import { ServiceAppointment } from '../utils/models';
import { forkJoin } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { NotificationService } from 'src/app/core/services/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { PartnerAPIService } from 'src/app/extensions/services/api/partner-api.service';

@Component({
  selector: 'timeslot-selector',
  standalone: false,
  templateUrl: './timeslot-selector.component.html',
  styleUrl: './timeslot-selector.component.scss',
})
export class TimeSlotSelectorComponent implements OnInit {
  dataAvailableDays: Date[] = [];
  dataLoaded: boolean = false;
  timeSlots: ServiceAppointment.TimeSlot[] = [];
  dayHasTimeSlots: boolean = false;
  scheduleModels: ServiceAppointment.ScheduleModel[] = [];

  selectedFilteringMode: 'NEAREST' | 'EARLIEST' = 'NEAREST';

  autocompleteValue: app.models.DictionaryDataModel = {
    id: undefined,
    value: undefined,
  } as app.models.DictionaryDataModel;
  autocompleteDatasource: app.models.DictionaryDataModel[] = [];

  summarySteps = [
    {
      label: 'book',
      code: 'BOOK',
      isValid: true,
      path: 'asset-driver-list',
    },
    {
      label: 'title',
      code: 'TIMESLOT',
      isValid: true,
    },
    { label: 'confirm', code: 'CONFIRM', isValid: false, disabled: true },
  ];

  currentStep = 1;
  returnUrl: string | null = null;

  constructor(
    public _dataService: AppointmentDataService,
    private _assetAPIService: AssetAPIService,
    private _partnerAPIService: PartnerAPIService,
    private _activatedRoute: ActivatedRoute,
    private _datePipe: DatePipe,
    private _router: Router,
    private _notificationService: NotificationService,
    private _translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.returnUrl = this._activatedRoute.snapshot.queryParamMap.get('returnUrl');
    this._dataService.saveModel.contractItemExternalEntityId =
      this._activatedRoute.snapshot.params['contractItemExternalEntityId'];

    this.summarySteps[0].path = `asset-driver-list/${this._dataService.saveModel.contractItemExternalEntityId}`;

    const serviceAppointmentBaseObject = JSON.parse(localStorage.getItem('SERVICE_APPOINTMENT')!);
    this._dataService.saveModel.registrationNumber = serviceAppointmentBaseObject.registrationNumber;
    this._dataService.saveModel.serviceName = serviceAppointmentBaseObject.serviceName;
    this._dataService.saveModel.serviceCode = serviceAppointmentBaseObject.serviceCode;

    //tbd: rethink serviceCode mapping (suggestion: pass through router params)
    // this._dataService.saveModel.serviceCode = this._activatedRoute.snapshot.params['serviceCode'];

    const source = forkJoin([
      this._assetAPIService.getTimeSlots(this._dataService.saveModel.scheduleDate),
      this._assetAPIService.getAvailableDates(),
      this._partnerAPIService.getClient(),
    ]);

    source.subscribe(([scheduleModels, availableDates, partnerModel]) => {
      this._dataService.saveModel.partnerExternalEntityId = partnerModel.partnerId;
      this.mapTimeSlots(scheduleModels);
      this.dataAvailableDays = availableDates.map(x => new Date(new Date(x).setHours(0, 0, 0, 0)));
      this.dataLoaded = true;
    });
  }

  onDayChange(date: Date) {
    this._assetAPIService.getTimeSlots(date).subscribe(scheduleModels => {
      this.mapTimeSlots(scheduleModels);
    });
  }

  private mapTimeSlots(scheduleModels: ServiceAppointment.ScheduleModel[]) {
    this.scheduleModels = scheduleModels.map(x => {
      return {
        ...x,
        isDisplayed: true,
        timeSlots: x.timeSlots.map(y => {
          return {
            ...y,
            startDate: new Date(y.startTime),
            endDate: new Date(y.endTime),
          } as ServiceAppointment.TimeSlot;
        }),
      } as ServiceAppointment.ScheduleModel;
    });

    this.dayHasTimeSlots = this.scheduleModels.some(x => x.timeSlots.some(x => x));

    this.autocompleteDatasource = this.scheduleModels.map(x => {
      return { id: x.id, value: x.name } as app.models.DictionaryDataModel;
    });
  }

  onTimeSlotDetailChange(timeSlotId: string) {
    this._dataService.saveModel.timeSlotId = timeSlotId;

    const scheduleModel = this.scheduleModels.find(x => x.timeSlots.some(y => y.timeSlotId == timeSlotId));
    if (scheduleModel) {
      this._dataService.saveModel.serviceLocationName = scheduleModel.name;
      const timeSlot = scheduleModel.timeSlots.find(x => x.timeSlotId == timeSlotId);

      let displayedDate = `${this._datePipe.transform(timeSlot.startTime, 'fullDate')} - ${this._datePipe.transform(timeSlot.startTime, 'shortTime')} - ${this._datePipe.transform(timeSlot.endTime, 'shortTime')}`;

      this._dataService.saveModel.timeslotDate = displayedDate;
    }
  }

  filterDisplayedTimeSlotGroupsCallback = (array: any[]): any[] => array.filter(x => x.isDisplayed);

  servicePartnerSelectedHandler(event?: any) {
    if (!event) {
      this.scheduleModels.map(x => (x.isDisplayed = true));
      return;
    }
    this.scheduleModels.find(x => x.name === event).isDisplayed = true;
    this.scheduleModels.filter(x => x.name !== event).map(x => (x.isDisplayed = false));
  }
  filterDataSource(event: any) {}

  currentStepChangeHandler(stepperIndex: number) {
    if (this.returnUrl) {
      this._router.navigate([decodeURIComponent(this.returnUrl)], {
        queryParams: { returnUrl: this.returnUrl.split('/')[1] },
      });
    } else {
      if (stepperIndex == 0) this._router.navigateByUrl(this.summarySteps[stepperIndex].path);
    }
  }

  confirmBtnClickHandler() {
    this._assetAPIService.saveServiceAppointment(this._dataService.saveModel).subscribe(x => {
      if (x.isSuccess) {
        this._notificationService.showSuccess(
          this._translateService.instant('common.success'),
          this._translateService.instant('common.success')
        );

        this.currentStepChangeHandler(0);
      } else {
        this._notificationService.showWarning(
          this._translateService.instant('common.warning'),
          this._translateService.instant('common.error')
        );
      }
    });
  }
}
